import React, { useState } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDAppBar from '../../../../BaseComponents/AppBar/EDAppBar';
import UserMenu from '../UserMenu/UserMenu';
import Logo from '../../../../Assets/Branding/Aveny_logo.svg';
import EDToolbar from '../../../../BaseComponents/Toolbar/EDToolbar';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import EDIconButton from '../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { AppNavBarToolbar } from '../Toolbar/AppNavBarToolbar';
import { IPathInfo } from '../../AppNavbar';
import HelpButton from '../HelpButton/HelpButton';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import { AvenyContext } from '../../../../Features/AvenyContext';
import RouterLink from '../../../RouterLink/RouterLink';
import { databaseMissingErrorText } from '../../../ActiveDatabaseContext/ActiveDatabaseContextUtils';
import EDTooltip from '../../../../BaseComponents/Tooltip/EDTooltip';
import { RutinSettingsContext } from '../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { Leveransstatus } from '../../../../Types/RutinInfoType';
import { rutiner } from '../../../../Features/Delsystem/_Common/Const/Rutiner';
import VersionLink from '../VersionLink/VersionLink';
import DeliveryStatusTag from '../../../DeliveryStatusTag/DeliveryStatusTag';
import HelpDialog from '../HelpButton/Components/HelpDialog';
import { APP_NAVBAR_HEIGHT_PX } from '../../../../BaseComponents/ThemeProvider/AvenyTheme';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../../../Features/Routing/Utils/routerUtils';
import EDBox from '../../../../BaseComponents/EDBox/EDBox';
import {
  RutinBackButtonRefs,
  RutinBreadcrumbs,
} from '../../../../Features/Delsystem/_Common/Components/RutinBreadcrumbs/RutinBreadcrumbs';
import {
  BreadcrumbsContext,
  HEADER,
  SEPARATOR,
} from '../../../../Features/Delsystem/_Common/Components/RutinBreadcrumbs/BreadcrumbsContext';
import DatabaseList from '../../../DatabaseList/DatabaseList';
import { LoginStateContext } from '../../../../Features/Login/Contexts/LoginStateContext/LoginStateContext';

interface IProps {
  activeDatabaseName: string | null;
  isLoggedIn: boolean;
  programLocationHeader: string;
  showEnhetSelector?: boolean;
  pathInfo?: IPathInfo;
  currentPath: string;
  toggleMainMenu: () => void;
  renderTaskMenuButton: (small?: boolean) => React.ReactNode;
  onUserMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  onUserMenuClose: () => void;
  openMiscMenu: (event: React.MouseEvent<HTMLElement>) => void;
  closeMiscMenu: () => void;
  miscMenuAnchor?: HTMLElement;
  miscMenuOpen: boolean;
  userMenuAnchorElement?: HTMLElement;
  toggleRightMenu: () => void;
  routineKey: string | undefined;
  routeIsAtLogin: boolean;
  formHasChanged: boolean;
  userMenuOpen: boolean;
  floatingTouchLayout?: boolean;
  isTouchDevice: boolean;
  classes?: any;
}

const styles = (theme: Theme) => ({
  toolbarLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    minHeight: APP_NAVBAR_HEIGHT_PX,
    width: '100%',
    [theme.breakpoints.only('xs')]: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: theme.spacing(6),
    },
    overflow: 'hidden',
  },
  logo: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    '& img': {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
  },
  logoButton: {
    marginRight: theme.spacing(0.5),
    marginLeft: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  xsCurrentPath: {
    flex: 1,
  },
  menuButton: {
    marginLeft: 0,
    marginRight: 0,
    color: theme.palette.common.white,
  },
});

const AppNavBarViewBase: React.FC<React.PropsWithChildren<IProps>> = props => {
  const {
    isLoggedIn = false,
    userMenuAnchorElement,
    classes,
    routeIsAtLogin,
    floatingTouchLayout,
    isTouchDevice,
    ...toolbarProps
  } = props;
  const { singleRoutineMode } = React.useContext(AvenyContext);
  const loginStateContext = React.useContext(LoginStateContext);
  const { activeRoutine } = React.useContext(RutinSettingsContext);
  const breadcrumbsContext = React.useContext(BreadcrumbsContext);
  const baseRouteValues = useBaseRouteValue();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  let directEditMode =
    rutiner[activeRoutine?.routine.rutinKod || '']?.directEditMode;
  const hideBreadcrumbs =
    rutiner[activeRoutine?.routine.rutinKod || '']?.hideBreadcrumbs;
  const menuDisabled = routeIsAtLogin || singleRoutineMode.enabled;
  const activeDatabase = loginStateContext.databases.find(
    database => database.name === props.activeDatabaseName
  );

  return (
    <React.Fragment>
      <EDAppBar>
        <EDToolbar disableGutters>
          <>
            <div className={classes.toolbarLeft}>
              {/* Back button - only on touch devices */}
              {props.floatingTouchLayout &&
                !routeIsAtLogin &&
                props.currentPath !== 'Startsida' && (
                  <EDBox
                    sx={{
                      top: 4,
                      width: 36,
                      height: APP_NAVBAR_HEIGHT_PX - 8,
                      minWidth: 36,
                    }}
                  >
                    <RutinBackButtonRefs
                      setBackbuttonRefs={breadcrumbsContext.setBackbuttonRefs}
                    />
                  </EDBox>
                )}

              {/* Menu button - not on touch devices */}
              {!routeIsAtLogin && !props.floatingTouchLayout && (
                <EDIconButton
                  tooltip={!menuDisabled ? 'Huvudmeny' : ''}
                  onClick={props.toggleMainMenu}
                  disabled={
                    directEditMode
                      ? false
                      : menuDisabled || !props.activeDatabaseName
                  }
                  disabledReason={
                    !props.activeDatabaseName
                      ? databaseMissingErrorText
                      : undefined
                  }
                  className={classes.menuButton}
                  icon={
                    <EDIcon iconType="menu" color="inherit" size="medium" />
                  }
                  testId="mainMenuButton"
                  margin="dense"
                  noTopMargin
                  noBottomMargin
                />
              )}

              {/* Logo button - on touch devices only on start page */}
              {(props.currentPath === 'Startsida' ||
                !props.floatingTouchLayout) && (
                <RouterLink
                  to={buildBasePath(baseRouteValues)}
                  replace={props.currentPath === 'Startsida'}
                  disabled={directEditMode ? false : menuDisabled}
                >
                  <EDIconButton
                    testId="startpanelButton"
                    tooltip={!menuDisabled ? 'Startpanel' : ''}
                    disabled={menuDisabled}
                    icon={
                      <img
                        src={Logo}
                        alt="Aveny Logo"
                        className={classes.logo}
                      />
                    }
                    margin="dense"
                    padding={props.floatingTouchLayout ? '0 0 0 6px' : 'none'}
                    className={classes.logoButton}
                  />
                </RouterLink>
              )}

              {/* Version link - only at login */}
              {routeIsAtLogin && <VersionLink />}

              {/* Title - on touch devices last part of breadcrumbs, otherwise program location header */}
              {props.floatingTouchLayout ? (
                props.routineKey ? (
                  <EDBox
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <EDTypography
                      component="div"
                      variant="subtitle2"
                      color="inherit"
                      sx={{ maxWidth: '100%' }}
                    >
                      {breadcrumbsContext.headerReferences.map((ref, i) => (
                        <React.Fragment key={i}>
                          <EDBox
                            sx={{ display: 'none' }}
                            ref={ref[SEPARATOR]}
                          />
                          <EDBox
                            sx={{
                              display:
                                i ===
                                (breadcrumbsContext.topBreadcrumbNavLevel ??
                                  breadcrumbsContext.topNavLevel)
                                  ? 'block'
                                  : 'none',
                              overflow: 'hidden',
                              padding: theme => theme.spacing(0, 1),
                            }}
                            ref={ref[HEADER]}
                          />
                        </React.Fragment>
                      ))}
                    </EDTypography>
                  </EDBox>
                ) : (
                  // floating layout but no routine
                  <EDBox
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <DatabaseList
                      databases={loginStateContext.databases}
                      selectedDatabases={activeDatabase ? [activeDatabase] : []}
                      size="small"
                      fixedWidth
                    />
                  </EDBox>
                )
              ) : (
                // not floating layout
                <>
                  {isTouchDevice && !props.routineKey ? (
                    <EDBox
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <DatabaseList
                        databases={loginStateContext.databases}
                        selectedDatabases={
                          activeDatabase ? [activeDatabase] : []
                        }
                        size="small"
                        fixedWidth
                      />
                    </EDBox>
                  ) : (
                    <EDTypography
                      variant="subtitle1"
                      color="inherit"
                      testId={`AppnavbarLocationHeader-${props.programLocationHeader}`}
                      bold={props.floatingTouchLayout}
                    >
                      {props.programLocationHeader}
                    </EDTypography>
                  )}
                  {activeRoutine &&
                    activeRoutine.routine.leveransstatus !==
                      Leveransstatus.Active && (
                      <DeliveryStatusTag
                        leveransstatus={activeRoutine.routine.leveransstatus}
                      />
                    )}
                  {activeRoutine?.routine.readonly && (
                    <EDTooltip title="Visar rutinen med läsbehörighet">
                      <EDBox sx={{ position: 'relative', bottom: 6 }}>
                        <EDIcon
                          iconType="eye_outline"
                          color="inherit"
                          size="small"
                        />
                      </EDBox>
                    </EDTooltip>
                  )}
                </>
              )}

              {/* HelpButton - on touch devices handled in AppNavBarSmallToolbar */}
              {!routeIsAtLogin && props.programLocationHeader && (
                <>
                  {!isTouchDevice && (
                    <HelpButton
                      programLocationHeader={props.programLocationHeader}
                      routineKey={props.routineKey}
                      setDialogOpen={setHelpDialogOpen}
                    />
                  )}
                  <HelpDialog
                    open={helpDialogOpen}
                    setOpen={setHelpDialogOpen}
                    programLocationHeader={props.programLocationHeader}
                  />
                </>
              )}
            </div>
            {/* Toolbar */}
            {!routeIsAtLogin && (
              <AppNavBarToolbar
                isLoggedIn={isLoggedIn}
                directEditMode={directEditMode}
                setHelpDialogOpen={setHelpDialogOpen}
                {...toolbarProps}
              />
            )}
            {/* User Menu */}
            {!routeIsAtLogin && (
              <UserMenu
                disabled={!isLoggedIn}
                singleRoutineEnabled={singleRoutineMode.enabled}
                onLogoutClick={props.onUserMenuClose}
                open={Boolean(userMenuAnchorElement)}
                anchorEl={userMenuAnchorElement}
                onClose={props.onUserMenuClose}
                closeMiscMenu={props.closeMiscMenu}
                formHasChanged={props.formHasChanged}
              />
            )}
          </>
        </EDToolbar>
      </EDAppBar>
      {!routeIsAtLogin &&
        props.currentPath !== 'Startsida' &&
        !props.floatingTouchLayout &&
        !hideBreadcrumbs && (
          <RutinBreadcrumbs
            headerRefs={breadcrumbsContext.headerReferences}
            setBackbuttonRefs={breadcrumbsContext.setBackbuttonRefs}
            setStepperRefs={breadcrumbsContext.setStepperRefs}
            setMenuRef={
              breadcrumbsContext.setMenuRef as unknown as React.Dispatch<
                React.SetStateAction<HTMLSpanElement | null>
              >
            }
            topIndex={breadcrumbsContext.topNavLevel ?? null}
          />
        )}
    </React.Fragment>
  );
};

export const AppNavBarView = withStyles(AppNavBarViewBase, styles);
